/**
 * Global client switcher (Cmd/Ctrl+K).
 *
 * ⌘K brings up a CLIENTS-ONLY picker — no pages/actions. Rendered as a bottom
 * slide-up panel (~1/3 viewport height) with clients in a responsive GRID
 * (multiple per row) so far more fit in view without scrolling. Type to filter;
 * the grid narrows live.
 *
 * Enter on a client → navigate. Cmd/Ctrl+Enter → open in a NEW TAB. The new-tab
 * handling tracks cmdk's highlighted item via the controlled value/onValueChange
 * API (cmdk marks the active item with aria-selected) so it's reliable.
 *
 * Controlled by default (App.tsx owns open/close); falls back to its own ⌘K
 * listener when uncontrolled.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command as CommandPrimitive } from "cmdk";
import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useClients } from "@/hooks/use-clients";

const MOD =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/i.test(navigator.platform) ? "⌘" : "Ctrl";

/**
 * Forgiving search normalization — identical to the rest of the app
 * (see ExerciseSearch.tsx `normalizeForSearch`). Lowercase, strip
 * hyphens/underscores, collapse whitespace. So "Jo-Anne", "jo anne",
 * and "joanne" all match the same client.
 */
const normalizeForSearch = (s: string) =>
  s.toLowerCase().replace(/[-_]/g, " ").replace(/\s+/g, " ").trim();

/**
 * Custom cmdk filter using the app's forgiving normalization. Each item's
 * `value` carries the client's name AND email, so typing part of either
 * surfaces the client. 1 = match, 0 = no match; equal scores keep render
 * order (alphabetical client list).
 */
const paletteFilter = (value: string, search: string): number => {
  const q = normalizeForSearch(search);
  if (!q) return 1;
  return normalizeForSearch(value).includes(q) ? 1 : 0;
};

/** Initials for the avatar bubble. */
const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

interface CommandPaletteProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Kept for call-site compatibility; unused now that the palette is clients-only. */
  onLogout?: () => void;
}

export function CommandPalette({ open: controlledOpen, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { data: clients = [] } = useClients();

  // Controlled / uncontrolled bridge.
  const isControlled = typeof controlledOpen === "boolean";
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? (controlledOpen as boolean) : internalOpen;
  const setOpen = (v: boolean) => {
    onOpenChange?.(v);
    if (!isControlled) setInternalOpen(v);
  };

  // Uncontrolled mode: own the ⌘K listener.
  useEffect(() => {
    if (isControlled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setInternalOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isControlled]);

  const [query, setQuery] = useState("");
  // cmdk's currently-highlighted item value (controlled) — resolves the active
  // client for ⌘+Enter → new tab without scraping the DOM.
  const [activeValue, setActiveValue] = useState("");
  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveValue("");
    }
  }, [open]);

  const clientItems = useMemo(
    () =>
      clients.map((c) => ({
        id: c.id,
        name: c.name || "Unnamed client",
        email: c.email || "",
      })),
    [clients]
  );

  // value → clientId map (cmdk lowercases values; match that transform).
  const clientValueToId = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of clientItems) {
      m.set(`${c.name} ${c.email}`.toLowerCase().trim(), c.id);
    }
    return m;
  }, [clientItems]);

  const openClient = (id: string, newTab: boolean) => {
    setOpen(false);
    if (newTab) window.open(`/clients/${id}`, "_blank", "noopener");
    else navigate(`/clients/${id}`);
  };

  // ⌘/Ctrl+Enter on the highlighted client → new tab. Resolve from cmdk's
  // controlled value first; fall back to the aria-selected DOM row.
  const handlePaletteKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;
    let clientId = clientValueToId.get(activeValue.toLowerCase().trim()) ?? null;
    if (!clientId) {
      const selected = e.currentTarget.querySelector<HTMLElement>(
        '[cmdk-item][aria-selected="true"], [cmdk-item][data-selected="true"]'
      );
      clientId = selected?.getAttribute("data-client-id") ?? null;
    }
    if (!clientId) return;
    e.preventDefault();
    e.stopPropagation();
    openClient(clientId, true);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="bottom"
        // ~1/3 viewport tall, flush to the bottom, no default padding.
        className="h-[33vh] min-h-[280px] p-0 gap-0 flex flex-col rounded-t-xl border-t"
      >
        <CommandPrimitive
          filter={paletteFilter}
          value={activeValue}
          onValueChange={setActiveValue}
          onKeyDownCapture={handlePaletteKeyDown}
          className="flex h-full w-full flex-col overflow-hidden bg-popover text-popover-foreground"
        >
          <CommandInput
            placeholder="Jump to a client by name or email…"
            value={query}
            onValueChange={setQuery}
          />
          {/* flex-1 so the list fills the panel and scrolls within it */}
          <CommandList className="flex-1 max-h-none bg-background p-2">
            <CommandEmpty className="py-8 text-center text-sm text-muted-foreground">
              No clients found.
            </CommandEmpty>

            {/* Grid: multiple clients per row so more fit without scrolling.
                cmdk arrow-key nav follows DOM order; the grid is purely visual. */}
            <CommandGroup className="[&_[cmdk-group-items]]:grid [&_[cmdk-group-items]]:grid-cols-2 [&_[cmdk-group-items]]:gap-1.5 sm:[&_[cmdk-group-items]]:grid-cols-3 lg:[&_[cmdk-group-items]]:grid-cols-4">
              {clientItems.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.name} ${c.email}`}
                  data-client-id={c.id}
                  onSelect={() => openClient(c.id, false)}
                  className="flex items-center gap-2 rounded-md border border-border/60 px-2.5 py-2 data-[selected=true]:border-primary/50 data-[selected=true]:bg-accent"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                    {initials(c.name)}
                  </span>
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium leading-tight">{c.name}</span>
                    {c.email && (
                      <span className="truncate text-xs text-muted-foreground leading-tight">{c.email}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>

          {/* Footer hint */}
          <div className="flex items-center justify-end gap-3 border-t px-3 py-1.5 text-[11px] text-muted-foreground">
            <span><kbd className="font-sans">↵</kbd> open</span>
            <span><kbd className="font-sans">{MOD} ↵</kbd> new tab</span>
            <span><kbd className="font-sans">esc</kbd> close</span>
          </div>
        </CommandPrimitive>
      </SheetContent>
    </Sheet>
  );
}

export default CommandPalette;
